import { Timestamp } from '@angular/fire/firestore';
import { IAdvancedFilterGroup } from '../../settings-new/models/general/advanced-filter-group.interface';
import { StockTakePopulationSortOrder } from './stock-take-population-sort.enum';
import { StockTakeStatus, STOCK_TAKE_LEGACY_PENDING_DOCUMENT_STATUS } from './stock-take-status.enum';

/** Document `status` field — includes legacy `PENDING` until backend migration completes. */
export type StockTakeDocumentStatus = StockTakeStatus | typeof STOCK_TAKE_LEGACY_PENDING_DOCUMENT_STATUS;

/**
 * Stock take document stored in Firestore.
 * Path: operational/stores_data/{storeId}/data/stock_takes/stock_takes/stock_takes/{id}
 */
export interface IStockTake {
  id: string;
  name: string;
  startTime: Timestamp;
  status: StockTakeDocumentStatus;
  userAssigned: string;
  /**
   * Physical bin or shelf location for this stock take — used to update binL on all counted items when processed
   */
  binLocation?: string;
  countedCount?: number;
  /**
   * Server-set completion instant (`SERVER_TIMESTAMP`) when the engine reaches a terminal outcome.
   * Written on SUCCESS and FAILED only.
   */
  endTime?: Timestamp | null;
  /**
   * Backend error detail when processing fails.
   * Explicit `null` on SUCCESS and SUBMITTED (from backend); optional only on legacy client-written docs.
   */
  errorMsg?: string | null;
  /** Number of lines in finalised chunk storage (set on SUBMITTED). */
  finalisedItemCount?: number;
  /**
   * Finalised figure layout written on process ({@link STOCK_TAKE_FINALISED_STORAGE_SCHEMA}).
   */
  finalisedStorageSchema?: number;
  /**
   * When true (default), Del-Col qty and Del-Col movement are included in variance.
   * When false, those fields are excluded from variance, the grid, and the summary report.
   * Absent on legacy documents — treat as true.
   */
  includeDelCol?: boolean;
  itemCount?: number;
  /**
   * When the stock take was last marked PREPARED. Cleared when it returns to IN_PROGRESS.
   * Absent on legacy prepared documents — dashboard age falls back to {@link startTime}.
   */
  preparedTime?: Timestamp | null;
  /**
   * Email of the user who last submitted / re-queued this stock take for processing.
   */
  processedByEmail?: string;
  /** Items marked RECOUNT — maintained on the parent document via increment(). */
  recountCount?: number;
  /** ID of the schedule entry that spawned this stock take, if any. */
  scheduleId?: string;
}

/**
 * DTO for creating a new stock take
 */
export interface IStockTakeCreate {
  name: string;
  userAssigned: string;
  /** Physical bin / shelf location (required on desktop, entered before counting on mobile) */
  binLocation?: string;
  /** If set, the stock take will be pre-populated with items not counted in this many days */
  countUncountedDays?: number;
  /**
   * @deprecated Use filterGroups. Kept for backward compatibility with single-filter creates.
   * When set, matching stock items are pre-populated via TypeSense (same as scheduled stock takes).
   * Not persisted on the stock take document.
   */
  filterGroup?: IAdvancedFilterGroup | null;
  /**
   * When set, matching stock items are pre-populated via TypeSense (combined with OR when multiple).
   * Not persisted on the stock take document.
   */
  filterGroups?: IAdvancedFilterGroup[];
  /** Sort order for advanced-filter population from Typesense */
  filterSortOrder?: StockTakePopulationSortOrder;
  /**
   * Whether Del-Col is included in variance for this stock take.
   * Defaults to true when omitted.
   */
  includeDelCol?: boolean;
  /**
   * Max items to add when populating from stock filters.
   * Not persisted on the stock take document.
   */
  itemLimit?: number;
  /** ID of the schedule entry that spawned this schedule-spawned stock take, if any. */
  scheduleId?: string;
  /** Maximum number of uncounted items to add when countUncountedDays is set */
  uncountedItemLimit?: number;
  /** Sort order for uncounted-item population from Typesense */
  uncountedSortOrder?: StockTakePopulationSortOrder;
}
